<template>
  <div id="submit-question-tab" class="tab is-full-height">
    <div v-if="isLoading" class="submit-question-loading">
      <LoadingIndicator message="Sending RFI…" />
    </div>

    <div class="label">Request for Information</div>

    <div v-if="isProjectArchived" class="notification is-warning is-light">
      This project is archived and no longer accepts RFIs.
    </div>

    <div v-else>
      <div v-if="legacyRFI">
        <o-field label="RFI for Trade"
                 addons-class="addons-class"
                 :variant="error('trade_id') ? 'danger' : ''"
                 :message="error('trade_id')"
                 v-if="bidders.length > 1"
        >
          <o-dropdown v-model="trade"
                      aria-role="list"
                      menu-class="menu-class"
                      scrollable
                      :max-height="300"
          >
            <template #trigger="{ active }">
              <div class="button is-secondary is-outlined">
                <span>{{ (trade?.full_label) || '--- Select ---'}}</span>
                <o-icon :icon="active ? 'caret-up' : 'caret-down'" pack="fas"></o-icon>
              </div>
            </template>

            <o-dropdown-item :value="null" v-if="trade !== null">--- Select ---</o-dropdown-item>
            <o-dropdown-item :value="bidder.trade" v-for="bidder in bidders">{{ bidder?.trade?.full_label }}</o-dropdown-item>
          </o-dropdown>
        </o-field>

        <o-field label="Subject*"
                 :variant="error('subject') ? 'danger' : ''"
                 :message="error('subject')"
        >
          <o-input v-model="subject" required maxlength="150"></o-input>
        </o-field>

        <o-field label="Body*"
                 :variant="error('body') ? 'danger' : ''"
                 :message="error('body')"
        >
          <o-input v-model="body" type="textarea" required></o-input>
        </o-field>

        <div class="has-text-right has-margin-top-large">
          <o-button class="is-text has-margin-right" @click="handleCancel">Clear</o-button>
          <o-button class="is-primary" @click="handleSend">Send RFI</o-button>
        </div>
      </div>

      <iframe v-else :src="rfiURL" width="100%" height="700" />
    </div>
  </div>
</template>

<script setup lang="ts">

import NotificationError from '@/use/NotificationError';
import NotificationSuccess from '@/use/NotificationSuccess';
import {computed, ref, toRefs, watch} from "vue";
import {storeToRefs} from "pinia";
import type {ProjectBidder} from "@/models/ProjectBidder";
import {useProjectRFIStore} from "@/stores/ProjectRFIStore";
import type {Trade} from "@/models/Trade";
import {useBidPackageProjectBidderStore} from "@/stores/BidPackageProjectBidderStore";
import {useProjectStore} from "@/stores/ProjectStore";
import {useCompanyStore} from "@/stores/CompanyStore";
import {useBidPackageStore} from "@/stores/BidPackageStore";
import LoadingIndicator from "@/components/LoadingIndicator.vue";

const legacyRFI = String(import.meta.env.VITE_LEGACY_RFI).toLowerCase() !== 'false';
const { bidPackageProjectBidders } = storeToRefs(useBidPackageProjectBidderStore());
const { project } = storeToRefs(useProjectStore());
const { isLoading } = storeToRefs(useProjectRFIStore());
const { company } = storeToRefs(useCompanyStore());
const { bidPackage } = storeToRefs(useBidPackageStore());
const projectRFIStore = useProjectRFIStore();
const errors = ref(<Record<any, any>>[]);
const body = ref('');
const trade = ref<Trade | null>(null);
const subject = ref('');

const props = withDefaults(defineProps<{
  bidPackageId: number | null,
}>(), {
  bidPackageId: null,
});
const {
  bidPackageId,
} = toRefs(props);

const bidders = computed<ProjectBidder[]>(() => {
  const isBidPackageContext = Number(bidPackageId.value) > 0;
  const isActiveBidPackage = !isBidPackageContext
    || Number(bidPackage.value?.id) === Number(bidPackageId.value);
  const availableTrades = isBidPackageContext
    ? (isActiveBidPackage ? bidPackage.value?.trades || [] : [])
    : (project.value?.open_invitation ? company.value?.trades || [] : []);
  const availableTradeIds = new Set(availableTrades.map((trade: Trade) => trade.id));
  const includedTradeIds = new Set<number>();

  const bidders = (bidPackageProjectBidders.value || []).filter((bidder: ProjectBidder) => {
    const tradeId = bidder.trade?.id;

    if (!tradeId || includedTradeIds.has(tradeId)) {
      return false;
    }

    if (isBidPackageContext && !availableTradeIds.has(tradeId)) {
      return false;
    }

    includedTradeIds.add(tradeId);
    return true;
  });

  if (isBidPackageContext) {
    return bidders;
  }

  return bidders.concat(availableTrades
    .filter((availableTrade: Trade) => !includedTradeIds.has(availableTrade.id))
    .map((availableTrade: Trade) => ({
      id: availableTrade.id,
      trade: availableTrade,
    })));
});

const isOpenProjectAndUninvited = computed(() => project.value?.open_invitation && !project.value.invited);
const isProjectArchived = computed(() => !!project.value?.archived_at);
const rfiEnabled = computed(() => company.value?.client?.show_rfi === true);

const rfiURL = computed(() => (legacyRFI) ? false : `${import.meta.env.VITE_RFI_APP_URL}subs-login?project_id=${project.value?.id}` + (bidPackageId.value ? `&bid_package_id=${bidPackageId.value}` : ''));

watch(bidders, (availableBidders) => {
  if (availableBidders.length === 1) {
    trade.value = availableBidders[0].trade || null;
    return;
  }

  if (!trade.value || !availableBidders.some((bidder) => bidder.trade?.id === trade.value?.id)) {
    trade.value = null;
  }
}, {immediate: true});

function clearErrors() {
  errors.value = [];
}

function error(key: string) {
  return errors.value?.find((error: any) => error.hasOwnProperty('key') && error.key === key)?.detail || '';
}

function handleCancel() {
  subject.value = '';
  body.value = '';
  trade.value = bidders.value.length === 1 ? bidders.value[0].trade || null : null;
}

function handleSend() {
  if (!project.value || !rfiEnabled.value) {
    return;
  }

  clearErrors();

  const payload: any = {
    subject: subject.value,
    body: body.value,
    trade_id: trade.value ? trade.value.id : null,
  };

  if (bidPackageId.value) {
    payload.bid_package_id = bidPackageId.value;
  }

  projectRFIStore.store(project.value.id, payload).then(() => {
    NotificationSuccess({
      message: `RFI Sent`,
    });

    handleCancel();
  }).catch((error: any) => {
    if (error.hasOwnProperty('errors')) {
      errors.value = error.errors;
    }
    else {
      NotificationError({
        message: error,
      });
    }
  });
}

</script>

<style lang="scss" scoped>

#submit-question-tab {
  position: relative;

  .submit-question-loading {
    align-items: center;
    background: rgba($white, .8);
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
    z-index: 2;
  }
}

</style>

<style lang="scss">

#submit-question-tab {
  .addons-class {
    display: block !important;
  }

  .dropdown {
    a {
      &.dropdown-item {
        justify-content: left;

        &.is-active {
          color: $white !important;
        }
      }
    }
  }
}

</style>
